import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ChevronDown, X, Check, Info, Scale, Ruler, Layers, Maximize, Lightbulb } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { categories, Product } from '../data/products';
import { MATERIAL_TRANSLATIONS } from '../data/materialTranslations';
import { useSEO } from '../utils/seo';
// Flatten all products
const allProducts = categories.flatMap(cat => cat.products.map(p => ({
  ...p,
  categoryName: cat.name
})));
export function CompareMaterials() {
  useSEO({
    title: 'Compare Gravel & Rock Materials — Puget Sound Terminology | 206Gravel',
    description: 'Compare crushed rock, drain rock, topsoil, and bark side by side, and translate national terms like #57 stone, crusher run, and Class 5 into the materials Washington yards actually sell.',
    path: '/compare'
  });
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  // Product-page comparisons always carry the current material plus two alternatives.
  // Keep that same three-material capacity at every screen size; the table can scroll.
  const maxSelections = 3;
  const [searchParams] = useSearchParams();
  const [selectedSlugs, setSelectedSlugs] = useState<string[]>([]);
  useEffect(() => {
    const requestedSlugs = (searchParams.get('products')?.split(',') ?? [searchParams.get('add') ?? '']).filter(Boolean);
    const validSlugs = requestedSlugs.filter((slug, index) => allProducts.some(product => product.slug === slug) && requestedSlugs.indexOf(slug) === index).slice(0, maxSelections);
    if (validSlugs.length > 0) setSelectedSlugs(validSlugs);
  }, [maxSelections, searchParams]);
  const handleSelect = (index: number, slug: string) => {
    const newSelections = [...selectedSlugs];
    newSelections[index] = slug;
    setSelectedSlugs(newSelections);
  };
  const handleClear = (index: number) => {
    const newSelections = [...selectedSlugs];
    newSelections.splice(index, 1);
    setSelectedSlugs(newSelections);
  };
  const clearAll = () => {
    setSelectedSlugs([]);
  };
  const getProduct = (slug: string) => allProducts.find(p => p.slug === slug);
  return <div className="pt-24 pb-16 bg-[#F2EFE8] dark:bg-[#222222] min-h-screen" data-id="element-2771" data-mp="32n9dmfinbgf7">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2772" data-mp="37lb8dxpfp46i">
        <div className="mb-8 flex flex-col md:flex-row md:items-end justify-between gap-4" data-id="element-2773" data-mp="37luduexwe03n">
          <div data-id="element-2774" data-mp="3vbou76hfe6sn">
            <Link to="/#materials" className="inline-flex items-center text-brand-muted hover:text-brand-orange transition-colors mb-4 font-bold text-sm" data-id="element-2775" data-mp="3v92hytjlb343">
              <ArrowLeft className="w-4 h-4 mr-1" data-id="element-2776" data-mp="3lbxwkvxkjboc" />
              Back to Materials Hub
            </Link>
            <h1 className="font-heading text-4xl md:text-5xl font-extrabold text-brand-charcoal dark:text-white tracking-tight" data-id="element-2777" data-mp="3qaragpgrp4p1">
              Compare Materials
            </h1>
            <p className="text-brand-muted dark:text-stone-300 mt-2 font-medium text-lg" data-id="element-2778" data-mp="2ex5mv8qk3b5m">
              Select up to {maxSelections} products to compare specs and
              details.
            </p>
          </div>
          {selectedSlugs.length > 0 && <button onClick={clearAll} className="text-sm font-bold text-brand-muted hover:text-brand-orange transition-colors self-start md:self-auto" data-id="element-2779" data-mp="rzxswo1ay4re">
              Clear All
            </button>}
        </div>

        {/* Selectors */}
        <div className="grid grid-cols-1 gap-4 mb-8 sm:grid-cols-3" data-id="element-2780" data-mp="e8j6e786rlfl">
          {Array.from({
          length: maxSelections
        }).map((_, i) => {
          const selectedSlug = selectedSlugs[i];
          const selectedProduct = selectedSlug ? getProduct(selectedSlug) : null;
          return <div key={i} className="bg-white/80 dark:bg-[#2a2a2a] rounded-xl border border-stone-200 dark:border-white/10 p-4 shadow-sm relative" data-id="element-2781" data-mp="iae8hdxxy2uf" data-mpi={i}>
                {selectedProduct ? <div className="flex items-center justify-between" data-id="element-2782" data-mp="3qod4gfkwxo7x">
                    <div className="flex items-center gap-3 overflow-hidden" data-id="element-2783" data-mp="3mnjb36opbh7n">
                      <img src={selectedProduct.images?.[0] || selectedProduct.image} alt={selectedProduct.name} className="w-12 h-12 rounded-lg object-cover shrink-0" data-id="element-2784" data-mp="38xt40etzxet" />
                      <div className="truncate" data-id="element-2785" data-mp="d6lrd0bnbn1q">
                        <p className="text-xs font-bold text-brand-muted uppercase tracking-wider truncate" data-id="element-2786" data-mp="d5tu7l2ehh9c">
                          {selectedProduct.categoryName}
                        </p>
                        <p className="text-sm font-extrabold text-brand-charcoal dark:text-white truncate" data-id="element-2787" data-mp="mvdgl0pbnt8y">
                          {selectedProduct.name}
                        </p>
                      </div>
                    </div>
                    <button onClick={() => handleClear(i)} className="p-1.5 text-brand-muted hover:text-brand-orange transition-colors shrink-0" data-id="element-2788" data-mp="vv0fnfukwmyu">
                      <X className="w-4 h-4" data-id="element-2789" data-mp="2xp9jnwk4rpkx" />
                    </button>
                  </div> : <div className="relative" data-id="element-2790" data-mp="32nb009q69asp">
                    <select className="w-full appearance-none bg-stone-50 dark:bg-[#2a2a2a] border border-stone-200 dark:border-white/10 rounded-lg py-3 px-4 text-sm font-bold text-brand-charcoal dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-orange" value="" onChange={e => handleSelect(i, e.target.value)} data-id="element-2791" data-mp="32o2x5ozf3gl3">
                      <option value="" disabled className="bg-white/90 dark:bg-[#2a2a2a] text-brand-charcoal dark:text-white" data-id="element-2792" data-mp="2lbw1deist8s5">
                        Select a product...
                      </option>
                      {categories.map(cat => <optgroup key={cat.name} label={cat.name} className="bg-white dark:bg-[#2a2a2a] text-brand-charcoal dark:text-white font-bold" data-id="element-2793" data-mp="3942tu2lekvma">
                          {cat.products.map(p => <option key={p.slug} value={p.slug} disabled={selectedSlugs.includes(p.slug)} className="bg-white dark:bg-[#2a2a2a] text-brand-charcoal dark:text-white disabled:text-stone-400 dark:disabled:text-stone-500" data-id="element-2794" data-mp="346jgd4wj4mve">
                              {p.name}
                            </option>)}
                        </optgroup>)}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-muted pointer-events-none" data-id="element-2795" data-mp="frj7ucvkqyas" />
                  </div>}
              </div>;
        })}
        </div>

        {/* Comparison Table / Cards */}
        {selectedSlugs.length === 0 ? <div className="bg-white dark:bg-[#2a2a2a] rounded-2xl border border-stone-200 dark:border-white/10 p-12 text-center shadow-sm" data-id="element-2796" data-mp="ar543710aaik">
            <div className="w-16 h-16 bg-stone-100 dark:bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-2797" data-mp="3s74u6wnx01ti">
              <Scale className="w-8 h-8 text-brand-muted" data-id="element-2798" data-mp="3j7q5o2rb61bn" />
            </div>
            <h3 className="text-xl font-extrabold text-brand-charcoal dark:text-white mb-2" data-id="element-2799" data-mp="3j5naz5f7nk0l">
              No products selected
            </h3>
            <p className="text-brand-muted dark:text-stone-400 font-medium" data-id="element-2800" data-mp="399aey0w70u2o">
              Use the dropdowns above to select materials to compare.
            </p>
          </div> : <div className="bg-white dark:bg-[#2a2a2a] rounded-2xl border border-stone-200 dark:border-white/10 shadow-sm overflow-hidden" data-id="element-2801" data-mp="2umvbo4dkxkaf">
            <div className="overflow-x-auto" data-id="element-2802" data-mp="2qmbc6u5ja4l4">
              <table className="min-w-[760px] w-full text-left border-collapse" data-id="element-2803" data-mp="2qnmgcv2w5ecr">
                <thead data-id="element-2804" data-mp="34mcg6ih3hs4t">
                  <tr className="border-b border-stone-200 dark:border-white/10 bg-stone-50 dark:bg-white/5" data-id="element-2805" data-mp="2zly4mqgac021">
                    <th className="p-4 font-bold text-brand-muted w-1/4" data-id="element-2806" data-mp="3dlhblfxd15cr">
                      Feature
                    </th>
                    {Array.from({
                  length: maxSelections
                }).map((_, i) => {
                  const slug = selectedSlugs[i];
                  const product = slug ? getProduct(slug) : null;
                  return <th key={i} className="w-1/4 border-l border-stone-200 p-4 font-extrabold text-brand-charcoal dark:border-white/10 dark:text-white" data-id="element-2807" data-mp="3id7q7y3byf9z" data-mpi={i}>
                          {product ? product.name : '-'}
                        </th>;
                })}
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-200 dark:divide-white/10" data-id="element-2808" data-mp="2cy1a5hwlhfgz">
                  {/* Price */}
                  <tr data-id="element-2809" data-mp="2hz79hykrg0zh">
                    <td className="p-4 font-bold text-brand-charcoal dark:text-stone-300 flex items-center gap-2" data-id="element-2810" data-mp="2mvxmgd19qggo">
                      Price / Yard
                    </td>
                    {Array.from({
                  length: maxSelections
                }).map((_, i) => {
                  const slug = selectedSlugs[i];
                  const product = slug ? getProduct(slug) : null;
                  return <td key={i} className="p-4 border-l border-stone-200 dark:border-white/10 font-extrabold text-brand-charcoal dark:text-white text-lg" data-id="element-2811" data-mp="245fvacw33sxf" data-mpi={i}>
                          {product ? `$${product.pricePerYard}` : '-'}
                        </td>;
                })}
                  </tr>

                  {/* Size */}
                  <tr data-id="element-2812" data-mp="1gf3pp8oba8a0">
                    <td className="p-4 font-bold text-brand-charcoal dark:text-stone-300" data-id="element-2813" data-mp="1geah025sl8p8">
                      <div className="flex items-center gap-2" data-id="element-2814" data-mp="3tpve834bqfum">
                        <Ruler className="w-4 h-4 text-brand-muted" data-id="element-2815" data-mp="3pq4hlis495i6" />
                        Size
                      </div>
                    </td>
                    {Array.from({
                  length: maxSelections
                }).map((_, i) => {
                  const slug = selectedSlugs[i];
                  const product = slug ? getProduct(slug) : null;
                  return <td key={i} className="p-4 border-l border-stone-200 dark:border-white/10 text-sm font-medium text-brand-muted dark:text-stone-300" data-id="element-2816" data-mp="360kaqdvla7cs" data-mpi={i}>
                          {product?.specs?.size || '-'}
                        </td>;
                })}
                  </tr>

                  {/* Weight */}
                  <tr data-id="element-2817" data-mp="2s1kmv8fwhwho">
                    <td className="p-4 font-bold text-brand-charcoal dark:text-stone-300" data-id="element-2818" data-mp="2s0t1eqg0pf54">
                      <div className="flex items-center gap-2" data-id="element-2819" data-mp="2n3j28j2ttp43">
                        <Scale className="w-4 h-4 text-brand-muted" data-id="element-2820" data-mp="2qfsl75rwfff9" />
                        Weight / Yard
                      </div>
                    </td>
                    {Array.from({
                  length: maxSelections
                }).map((_, i) => {
                  const slug = selectedSlugs[i];
                  const product = slug ? getProduct(slug) : null;
                  return <td key={i} className="p-4 border-l border-stone-200 dark:border-white/10 text-sm font-medium text-brand-muted dark:text-stone-300" data-id="element-2821" data-mp="27hg7d6q1lhdc" data-mpi={i}>
                          {product?.specs?.weightPerYard || '-'}
                        </td>;
                })}
                  </tr>

                  {/* Compaction */}
                  <tr data-id="element-2822" data-mp="2bis0vnc1emqt">
                    <td className="p-4 font-bold text-brand-charcoal dark:text-stone-300" data-id="element-2823" data-mp="2bhi36z93jgkp">
                      <div className="flex items-center gap-2" data-id="element-2824" data-mp="3rw023elznyjk">
                        <Layers className="w-4 h-4 text-brand-muted" data-id="element-2825" data-mp="3myq5nf06mmm8" />
                        Compaction
                      </div>
                    </td>
                    {Array.from({
                  length: maxSelections
                }).map((_, i) => {
                  const slug = selectedSlugs[i];
                  const product = slug ? getProduct(slug) : null;
                  return <td key={i} className="p-4 border-l border-stone-200 dark:border-white/10 text-sm font-medium text-brand-muted dark:text-stone-300" data-id="element-2826" data-mp="3qg4cy1ixo3wg" data-mpi={i}>
                          {product?.specs?.compaction || '-'}
                        </td>;
                })}
                  </tr>

                  {/* Coverage */}
                  <tr data-id="element-2827" data-mp="8baze8tnu73u">
                    <td className="p-4 font-bold text-brand-charcoal dark:text-stone-300" data-id="element-2828" data-mp="89q0y848640p">
                      <div className="flex items-center gap-2" data-id="element-2829" data-mp="d8b5ukuf9bvh">
                        <Maximize className="w-4 h-4 text-brand-muted" data-id="element-2830" data-mp="10zgjctcw3s2m" />
                        Coverage
                      </div>
                    </td>
                    {Array.from({
                  length: maxSelections
                }).map((_, i) => {
                  const slug = selectedSlugs[i];
                  const product = slug ? getProduct(slug) : null;
                  return <td key={i} className="p-4 border-l border-stone-200 dark:border-white/10 text-sm font-medium text-brand-muted dark:text-stone-300" data-id="element-2831" data-mp="15npo8i8wodur" data-mpi={i}>
                          {product?.specs?.coverage || '-'}
                        </td>;
                })}
                  </tr>

                  {/* Best For */}
                  <tr data-id="element-2832" data-mp="rvt0eqe9sq46">
                    <td className="p-4 font-bold text-brand-charcoal dark:text-stone-300" data-id="element-2833" data-mp="rvj98zhhocww">
                      <div className="flex items-center gap-2" data-id="element-2834" data-mp="my9mjrlhifcr">
                        <Check className="w-4 h-4 text-brand-muted" data-id="element-2835" data-mp="hyex7shcxxtt" />
                        Best For
                      </div>
                    </td>
                    {Array.from({
                  length: maxSelections
                }).map((_, i) => {
                  const slug = selectedSlugs[i];
                  const product = slug ? getProduct(slug) : null;
                  return <td key={i} className="p-4 border-l border-stone-200 dark:border-white/10" data-id="element-2836" data-mp="2kn50uaekz6jd" data-mpi={i}>
                          {product?.bestFor ? <div className="flex flex-wrap gap-1.5" data-id="element-2837" data-mp="2tlqje5k1ylk5">
                              {product.bestFor.map(tag => <span key={tag} className="inline-block px-2 py-1 bg-stone-100 dark:bg-white/10 text-brand-charcoal dark:text-stone-300 text-xs font-bold rounded-md" data-id="element-2838" data-mp="2ykbh9ybu0t5f">
                                  {tag}
                                </span>)}
                            </div> : '-'}
                        </td>;
                })}
                  </tr>

                  {/* Pro Tip */}
                  <tr data-id="element-2839" data-mp="2flfs51etz4yg">
                    <td className="p-4 font-bold text-brand-charcoal dark:text-stone-300" data-id="element-2840" data-mp="2fmr3pneiph04">
                      <div className="flex items-center gap-2" data-id="element-2841" data-mp="1i73h8adm9ne9">
                        <Lightbulb className="w-4 h-4 text-brand-orange" data-id="element-2842" data-mp="1n5esmpjk85yl" />
                        Pro Tip
                      </div>
                    </td>
                    {Array.from({
                  length: maxSelections
                }).map((_, i) => {
                  const slug = selectedSlugs[i];
                  const product = slug ? getProduct(slug) : null;
                  return <td key={i} className="p-4 border-l border-stone-200 dark:border-white/10 text-sm font-medium text-brand-muted dark:text-stone-400 italic" data-id="element-2843" data-mp="2gu7lvxwn9yw" data-mpi={i}>
                          {product?.proTip ? `"${product.proTip}"` : '-'}
                        </td>;
                })}
                  </tr>

                  {/* View Details Link */}
                  <tr data-id="element-2844" data-mp="lcu1xdecupln">
                    <td className="p-4" data-id="element-2845" data-mp="ldbxe7t70zsi"></td>
                    {Array.from({
                  length: maxSelections
                }).map((_, i) => {
                  const slug = selectedSlugs[i];
                  const product = slug ? getProduct(slug) : null;
                  return <td key={i} className="p-4 border-l border-stone-200 dark:border-white/10 text-center" data-id="element-2846" data-mp="2j4omb7seyofe" data-mpi={i}>
                          {product && <Link to={`/shop/${product.slug}`} className="inline-block w-full py-2 bg-stone-100 dark:bg-white/10 hover:bg-stone-200 dark:hover:bg-white/20 text-brand-charcoal dark:text-white text-sm font-bold rounded-lg transition-colors" data-id="element-2847" data-mp="29083q6njut7w">
                              View Details
                            </Link>}
                        </td>;
                })}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>}

        {/* National → Puget Sound terminology. Customers who moved here, or
            followed an out-of-state how-to, arrive asking for "#57 stone" or
            "crusher run" — the right material under the wrong name. This is
            the translation layer, and it captures those searches without
            putting national vocabulary anywhere else on the site. */}
        <section className="mt-16" data-id="element-2848" data-mp="3fool6d2lgr0t">
          <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-brand-charcoal dark:text-white tracking-tight" data-id="element-2849" data-mp="3fnmw34oqu403">
            Told to get “#57 stone”? Here's what to order in Washington
          </h2>
          <p className="mt-3 max-w-3xl text-brand-muted dark:text-stone-300 font-medium" data-id="element-2850" data-mp="396lezvzuqq4i">
            Gravel is named differently in every region. National how-to
            guides, out-of-state contractors, and big-box spec sheets use
            terms no Puget Sound yard sells by. Here's the translation — same
            rock, local name.
          </p>

          <div className="mt-6 bg-white dark:bg-[#2a2a2a] rounded-2xl border border-stone-200 dark:border-white/10 shadow-sm overflow-hidden" data-id="element-2851" data-mp="3ry4gfye89w4f">
            <ul className="divide-y divide-stone-200 dark:divide-white/10" data-id="element-2852" data-mp="3n5l22z7puwyk">
              {MATERIAL_TRANSLATIONS.map(row => {
              const product = getProduct(row.localSlug);
              if (!product) return null;
              return <li key={row.localSlug} className="grid md:grid-cols-12 gap-x-6 gap-y-2 p-5" data-id="element-2853" data-mp="es4bpktar25n">
                    <div className="md:col-span-4" data-id="element-2854" data-mp="m38xwxmgm0s3">
                      <span className="block text-[11px] font-bold uppercase tracking-wider text-brand-muted dark:text-stone-400" data-id="element-2855" data-mp="m4k2v0rn5eks">
                        If you were told
                      </span>
                      <span className="mt-1 block text-sm font-bold text-brand-charcoal dark:text-white" data-id="element-2856" data-mp="vzlty25h8qq0">
                        {row.national.join(' · ')}
                      </span>
                    </div>
                    <div className="md:col-span-3" data-id="element-2857" data-mp="14zscw3jy2iwk">
                      <span className="block text-[11px] font-bold uppercase tracking-wider text-brand-muted dark:text-stone-400" data-id="element-2858" data-mp="1a068un89u2e8">
                        Order this
                      </span>
                      <Link to={`/shop/${product.slug}`} className="mt-1 inline-flex items-baseline gap-2 text-sm font-extrabold text-brand-charcoal dark:text-white hover:text-brand-orange dark:hover:text-brand-orange transition-colors" data-id="element-2859" data-mp="1jr0xfy8zpe2u">
                        {product.name}
                        <span className="text-xs font-bold text-brand-muted dark:text-stone-400" data-id="element-2860" data-mp="2e2e719bawbtg">
                          ${product.pricePerYard}/yd
                        </span>
                      </Link>
                    </div>
                    <p className="md:col-span-5 text-sm font-medium text-brand-muted dark:text-stone-300 leading-relaxed" data-id="element-2861" data-mp="2nziudcgvudbu">
                      {row.note}
                    </p>
                  </li>;
            })}
            </ul>
          </div>

          <p className="mt-4 text-sm font-medium text-brand-muted dark:text-stone-400" data-id="element-2862" data-mp="1v9jyoy86nhy7">
            Still not sure which one your job needs?{' '}
            <Link to="/help" className="font-bold text-brand-charcoal dark:text-white hover:text-brand-orange dark:hover:text-brand-orange transition-colors underline underline-offset-2" data-id="element-2863" data-mp="252rdf9otcaay">
              Answer four questions
            </Link>{' '}
            and we'll pick it for you, or call dispatch at{' '}
            <a href="tel:206-650-0129" className="font-bold text-brand-charcoal dark:text-white hover:text-brand-orange dark:hover:text-brand-orange transition-colors underline underline-offset-2" data-id="element-2864" data-mp="17lixkadc33st">
              (206) 650-0129
            </a>
            .
          </p>
        </section>
      </div>
    </div>;
}